import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

/* Page metadata. Titles read "Page name | EdAI Systems". */
export function Seo({ title, description }) {
  const full = title ? `${title} | EdAI Systems` : 'EdAI Systems: The Standard of Knowledge';
  return (
    <Helmet>
      <title>{full}</title>
      {description && <meta name="description" content={description} />}
      <meta property="og:title" content={full} />
      {description && <meta property="og:description" content={description} />}
    </Helmet>
  );
}

/* A small label with the gold mark. Sentence case, never all caps. */
export function Mark({ children, dark = false }) {
  return (
    <p className={`flex items-center gap-2 text-sm font-medium ${dark ? 'text-slate-dark-text' : 'text-slate'}`}>
      <span aria-hidden="true" className="inline-block h-2 w-2 rounded-[1px] bg-seal" />
      {children}
    </p>
  );
}

/* Standard page container. */
export function Container({ children, className = '' }) {
  return <div className={`mx-auto w-full max-w-site px-6 md:px-10 ${className}`}>{children}</div>;
}

/* A navy stat band. Serif numerals carry the weight. */
export function StatBand({ stats, footnote }) {
  if (!stats || stats.length === 0) return null;
  return (
    <section className="bg-ink-deep py-14 text-paper-dark-text">
      <Container>
        <dl className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label}>
              <dd className="font-serif text-4xl md:text-5xl">{s.value}</dd>
              <dt className="mt-2 max-w-[26ch] text-sm leading-relaxed text-slate-dark-text">{s.label}</dt>
            </div>
          ))}
        </dl>
        {footnote && <p className="mt-8 max-w-measure text-sm leading-relaxed text-slate-dark-text">{footnote}</p>}
      </Container>
    </section>
  );
}

/* The demo film slot. Renders a real player when media.video is set,
   otherwise a designed placeholder that tells the visitor a film is coming. */
export function VideoSlot({ video, title, text }) {
  return (
    <div>
      {video && video.embed ? (
        <div className="aspect-video overflow-hidden rounded-media border border-hairline-dark">
          <iframe
            src={video.embed}
            title={title}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : video && video.src ? (
        <video
          controls
          preload="metadata"
          poster={video.poster || undefined}
          className="aspect-video w-full rounded-media border border-hairline-dark bg-ink object-cover"
        >
          <source src={video.src} />
          Your browser does not support embedded video.
        </video>
      ) : (
        <div className="flex aspect-video flex-col items-center justify-center rounded-media border border-hairline-dark bg-ink px-8 text-center">
          <span aria-hidden="true" className="mb-4 inline-block h-3 w-3 rounded-[1px] bg-seal" />
          <p className="font-serif text-xl text-paper-dark-text">Demo film in production</p>
          <p className="mt-2 max-w-[44ch] text-sm leading-relaxed text-slate-dark-text">
            {text || 'A recorded walkthrough is being produced for this page. Request a live demo in the meantime.'}
          </p>
        </div>
      )}
      {video && video.caption && <p className="mt-3 text-sm text-slate-dark-text">{video.caption}</p>}
    </div>
  );
}

/* Screenshot gallery: a main image with thumbnails, keyboard accessible. */
export function ScreenshotGallery({ screenshots }) {
  const [active, setActive] = useState(0);
  if (!screenshots || screenshots.length === 0) return null;
  const current = screenshots[active];
  return (
    <div>
      <figure>
        <img
          src={current.src}
          alt={current.alt}
          className="w-full rounded-media border border-hairline-dark bg-white"
          loading="lazy"
        />
        <figcaption className="mt-3 text-sm text-slate-dark-text">{current.caption}</figcaption>
      </figure>
      {screenshots.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-3">
          {screenshots.map((s, i) => (
            <button
              key={s.src}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show screenshot: ${s.caption}`}
              aria-pressed={i === active}
              className={`overflow-hidden rounded border transition-colors ${
                i === active ? 'border-seal' : 'border-hairline-dark hover:border-seal-soft'
              }`}
            >
              <img src={s.src} alt="" className="h-14 w-24 object-cover object-top" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* The dark "see it" band used on product pages: video slot plus screenshots. */
export function MediaSection({ media, name }) {
  const hasShots = media.screenshots && media.screenshots.length > 0;
  return (
    <section className="bg-ink py-20 text-paper-dark-text">
      <Container>
        <Mark dark>See it</Mark>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl">{media.videoTitle}</h2>
        <p className="mt-4 max-w-measure leading-relaxed text-slate-dark-text">{media.videoText}</p>
        <div className={`mt-10 grid gap-10 ${hasShots ? 'lg:grid-cols-2' : 'lg:max-w-measure-wide'}`}>
          <VideoSlot video={media.video} title={`${name} demo`} text={media.videoText} />
          {hasShots && <ScreenshotGallery screenshots={media.screenshots} />}
        </div>
      </Container>
    </section>
  );
}
